import { CARD_API_ENDPOINT } from '@/config/consts'
import { useCardSolverConfigs } from '@/domain/state'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import type { SolverType } from '../constants'
import { createSmartFetch } from './useAuthenticatedMutation'
import type { UserCard } from './useUserCards'

export interface CreateCardRequest {
  solverType: SolverType | string
  chainID: number
  cardSolverConfig: Record<string, unknown>
}

interface CreateCardResponse {
  data: UserCard
}

export function useCreateCard() {
  const { address } = useAccount()
  const { upsertCardSolverConfig } = useCardSolverConfigs()
  const queryClient = useQueryClient()
  const createCardUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/create?card_type=virtual`

  const mutation = useMutation<CreateCardResponse, Error, CreateCardRequest>({
    mutationKey: ['create-card-manual'],
    mutationFn: async (request: CreateCardRequest) => {
      const fetchFn = createSmartFetch(createCardUrl, { method: 'POST' })
      return fetchFn(request)
    },
    onSuccess: (response, request) => {
      if (address && response.data) {
        upsertCardSolverConfig(address, {
          cardID: response.data.cardID,
          solverType: response.data.solverType,
          chainID: response.data.chainID,
          cardSolverConfig: response.data.solverConfig ?? request.cardSolverConfig,
          status: response.data.status,
          label: response.data.label,
          last4: response.data.last4,
        })
      }
      queryClient.invalidateQueries({ queryKey: ['user-cards', address] })
    },
  })

  return {
    createCard: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data?.data,
    reset: mutation.reset,
  }
}
